import * as React from "react";
import { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { CON_DEFAULT_PNG, CON_STATUS_ACTIVE } from "../../utils/sys/Constants";
import { FormatDate } from "../../utils/common/DateUtils";
import { GRN_API_12 } from "../../api/ApiModel";
import { externalgetApi } from "../../services/externalApis";
import { AnyForNow } from "../../features/grn/types";
import { InsertDriveFile as InsertDriveFileIcon } from "@mui/icons-material";

interface GrnStatusHistoryProps {
  trackingDetails: AnyForNow[];
  grncode: string;
}

const fmtDate = (val: string | Date | null | undefined) => {
  if (!val) return "-";
  const d = new Date(val);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const GrnReturnStatusHistory: React.FC<GrnStatusHistoryProps> = ({
  trackingDetails,
}) => {
  const theme = useTheme();
  const [grnStatusTypes, setGrnStatusTypes] = useState<AnyForNow[]>([]);
  const [selectedStep, setSelectedStep] = React.useState<AnyForNow | null>(null);
  const detailsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchGrnStatusTypes();
  }, []);

  const fetchGrnStatusTypes = async () => {
    try {
      const result = await externalgetApi(GRN_API_12, "GET", {
        STS: CON_STATUS_ACTIVE,
      });
      if (Array.isArray(result)) {
        setGrnStatusTypes(result);
      }
    } catch (err) {
      console.error("Error fetching GRN status types:", err);
    }
  };

  const handleStepClick = (step: AnyForNow) => {
    const isSame =
      selectedStep?.endate === step.endate &&
      selectedStep?.trsts === step.trsts;
    setSelectedStep(isSame ? null : step);
    if (!isSame) {
      setTimeout(() => {
        detailsRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 50);
    }
  };

  const selectedStatusInfo = grnStatusTypes.find(
    (s) => String(s.trsts) === String(selectedStep?.trsts)
  );
  const selectedTracksts = selectedStatusInfo?.trenuserremark || "-";

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        mt: 3,
      }}
    >
      <Paper sx={{ p: 2, mb: 0, overflow: "hidden" }}>
        <Box sx={{ p: 1 }}>
          {/* Header */}
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              mb: 2,
              color: theme.palette.text.primary,
            }}
          >
            • GRN Tracking Details
          </Typography>

          {/* Tracking Stepper */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              overflowX: "auto",
              py: 1,
            }}
          >
            {trackingDetails.map((step, index) => {
              const statusInfo = grnStatusTypes.find(
                (s) => String(s.trsts) === String(step.trsts)
              );
              const tracksts = statusInfo?.trenuserremark || "";
              const isSelected =
                selectedStep?.endate === step.endate &&
                selectedStep?.trsts === step.trsts;

              return (
                <React.Fragment key={index}>
                  <Box sx={{ textAlign: "center", minWidth: 90, px: 1 }}>
                    <Tooltip
                      arrow
                      placement="top"
                      title={
                        <Box sx={{ fontSize: 11 }}>
                          <Typography sx={{ fontSize: 11, fontWeight: 600, mb: 0.5 }}>
                            {tracksts}
                          </Typography>
                          <Typography sx={{ fontSize: 11 }}>
                            <b>User:</b> {step.enuser || "UNKNOWN"}
                          </Typography>
                          <Typography sx={{ fontSize: 11 }}>
                            <b>Date:</b> {FormatDate(step.endate) || "UNKNOWN"}
                          </Typography>
                          <Typography sx={{ fontSize: 11 }}>
                            <b>Time:</b>{" "}
                            {step.endate
                              ? new Date(step.endate).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                })
                              : "UNKNOWN"}
                          </Typography>
                        </Box>
                      }
                    >
                      <Box
                        onClick={() => handleStepClick(step)}
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          background:
                            index === trackingDetails.length - 1
                              ? "#eb6a1f"
                              : "#28A745",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontSize: theme.typography.subtitle_xs,
                          margin: "0 auto",
                          cursor: "pointer",
                          transition: "transform 0.2s, box-shadow 0.2s",
                          ...(isSelected && {
                            outline: "3px solid #887e7e",
                            outlineOffset: "3px",
                            boxShadow: "0 0 0 5px rgba(245, 242, 241, 0.2)",
                            transform: "scale(1.18)",
                          }),
                          "&:hover": {
                            transform: isSelected ? "scale(1.18)" : "scale(1.12)",
                            boxShadow: "0 0 0 4px rgba(235,106,31,0.15)",
                          },
                        }}
                      >
                        ✓
                      </Box>
                    </Tooltip>

                    <Typography
                      sx={{
                        fontSize: 10,
                        mt: 0.8,
                        fontWeight: 500,
                        color: theme.palette.text.secondary,
                        wordBreak: "break-word",
                      }}
                    >
                      S{index + 1}
                    </Typography>
                  </Box>

                  {index !== trackingDetails.length - 1 && (
                    <Box
                      sx={{
                        height: 2,
                        flex: 1,
                        background: theme.palette.divider,
                        minWidth: 40,
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </Box>

          {/* ── Inline Details Panel (no Dialog) ── */}
          {selectedStep && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Box
                ref={detailsRef}
                sx={{
                  width: "50%",
                  p: 1.5,
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`,
                  background: theme.palette.background.default,
                  fontSize: 14,
                }}
              >
                {/* Panel header */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                   
                  </Typography>
                  <Box
                    onClick={() => setSelectedStep(null)}
                    sx={{
                      cursor: "pointer",
                      fontSize: 16,
                      lineHeight: 1,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    ✕
                  </Box>
                </Box>

                <table
                  style={{ width: "100%", fontSize: 14, borderCollapse: "collapse" }}
                >
                  <tbody>
                    {[
                      { label: "Status", value: selectedTracksts },
                      { label: "Date", value: fmtDate(selectedStep.endate) },
                      {
                        label: "Time",
                        value: new Date(selectedStep.endate).toLocaleTimeString(
                          [],
                          { hour: "2-digit", minute: "2-digit", hour12: true }
                        ),
                      },
                      { label: "Updated User", value: selectedStep.enuser || "-" },
                      { label: "Reason", value: selectedStep.reason || "-" },
                      { label: "Remark", value: selectedStep.remark || "-" },
                    ].map(({ label, value }) => (
                      <tr key={label}>
                        <td
                          style={{
                            fontWeight: 600,
                            padding: "5px 4px",
                            width: "40%",
                            color: "inherit",
                          }}
                        >
                          {label}
                        </td>
                        <td style={{ padding: "5px 4px" }}>{value}</td>
                      </tr>
                    ))}

                    {/* Attachment row */}
                    <tr>
                      <td
                        style={{
                          fontWeight: 600,
                          padding: "5px 4px",
                          verticalAlign: "top",
                        }}
                      >
                        Attachment
                      </td>
                      <td style={{ padding: "5px 4px" }}>
                        {(() => {
                          if (!selectedStep?.docpaths) return "-";
                          let docs: AnyForNow[] = [];
                          try {
                            docs = JSON.parse(selectedStep.docpaths);
                          } catch {
                            return "-";
                          }
                          const validDocs = docs.filter(
                            (d) =>
                              d.FILEPATH && d.FILEPATH !== CON_DEFAULT_PNG
                          );
                          if (validDocs.length === 0) return "-";
                          return (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                              {validDocs.map((doc, i) => (
                                <Box
                                  key={i}
                                  component="a"
                                  href={doc.FILEPATH}
                                  target="_blank"
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    fontSize: theme.typography.subtitle_xs,
                                    fontWeight: 600,
                                    color: theme.palette.text.primary,
                                    textDecoration: "none",
                                  }}
                                >
                                  <InsertDriveFileIcon sx={{ fontSize: 20 }} />
                                </Box>
                              ))}
                            </Box>
                          );
                        })()}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default GrnReturnStatusHistory;
